import { Message, Attachment } from '../types';

/** Raw sender shape from various backend sources. */
interface RawSenderInput {
  id?: string;
  senderId?: string;
  type?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  avatarColor?: string;
  profileImageUrl?: string;
}

/** Minimal attachment shape accepted by preview/normalization helpers. */
interface AttachmentLike {
  type?: string;
  mimeType?: string;
  name?: string;
  url?: string;
}

/**
 * Union of all known message fields across REST, WebSocket, and legacy inputs.
 * The normalizer reads whichever fields are present and produces a typed Message.
 */
export interface RawMessageInput {
  id?: string;
  conversationId?: string;
  content?: string | { text?: string; message?: string } | null;
  /** When string: content text. When object: nested message payload (WebSocket). */
  message?: string | RawMessageInput;
  text?: string;
  messageType?: string;
  type?: string;
  deliveryChannel?: string;
  timestamp?: number;
  sentAt?: string | Date | null;
  status?: string;
  isLogged?: boolean;
  senderId?: string;
  senderType?: string;
  sender?: RawSenderInput;
  senderSnapshot?: RawSenderInput;
  attachments?: AttachmentLike[];
  media_urls?: string[];
  metadata?: Record<string, unknown>;
  subtype?: string;
  data?: Record<string, unknown>;
}

/** Compute a robust epoch-ms timestamp from various field conventions. */
export const getMessageTimestamp = (
  input: { timestamp?: number; sentAt?: string | Date | null } | null | undefined
): number => {
  const ts = input?.timestamp || (input?.sentAt ? new Date(input.sentAt).getTime() : undefined);
  // Avoid Date.now() during SSR - use 0 as fallback
  return typeof ts === 'number' && !isNaN(ts) ? ts : (typeof window !== 'undefined' ? Date.now() : 0);
};

export const isSystemMessage = (
  input: { messageType?: string; type?: string } | null | undefined
): boolean => {
  if (!input) return false;
  const messageType = input.messageType || input.type;
  return messageType === 'system';
};

export const compareMessagesByTimestamp = (
  a: { timestamp?: number; sentAt?: string; metadata?: Record<string, unknown>; id?: string },
  b: { timestamp?: number; sentAt?: string; metadata?: Record<string, unknown>; id?: string }
): number => {
  const aTime = a.timestamp || (a.sentAt ? new Date(a.sentAt).getTime() : 0);
  const bTime = b.timestamp || (b.sentAt ? new Date(b.sentAt).getTime() : 0);
  if (aTime !== bTime) return aTime - bTime;
  const aSeq = a.metadata?._seq as number | undefined;
  const bSeq = b.metadata?._seq as number | undefined;
  if (aSeq !== undefined && bSeq !== undefined) return aSeq - bSeq;
  return (a.id || '').localeCompare(b.id || '');
};

export const buildMessagePreview = (
  message: { content?: string | { text?: string; message?: string } | null; attachments?: AttachmentLike[]; media_urls?: string[] } | null | undefined
): string => {
  const rawContent = message?.content;
  const content = (typeof rawContent === 'string' ? rawContent : (typeof rawContent === 'object' && rawContent !== null ? (rawContent.text || rawContent.message || '') : '')).trim();
  if (content) return content;

  const attachments: AttachmentLike[] = Array.isArray(message?.attachments)
    ? message.attachments
    : (Array.isArray(message?.media_urls)
        ? message.media_urls.map((url: string) => ({ url }))
        : []);

  if (attachments.length === 0) return '';

  return 'Attachment:';
};

/** Normalize attachments, including legacy media_urls. */
const normalizeAttachments = (input: RawMessageInput): Attachment[] => {
  if (Array.isArray(input.attachments) && input.attachments.length > 0) {
    return input.attachments as Attachment[];
  }
  if (Array.isArray(input.media_urls)) {
    const baseId = input.id || 'msg';
    return input.media_urls.map((url: string, idx: number): Attachment => ({
      id: `media_${baseId}_${idx}`,
      type: 'image',
      url,
      name: url.split('/').pop() || `media_${idx}`,
      size: 0,
    }));
  }
  return [];
};

/** Determine sender fields and senderType from raw input. */
const deriveSender = (input: RawMessageInput) => {
  const rawSenderId = input.sender?.id || input.senderId || '';
  const messageType = input.messageType;
  const deliveryChannel = input.deliveryChannel;

  const rawSenderType = input.sender?.type || input.senderSnapshot?.type || input.senderType;
  let derivedSenderType: 'user' | 'contact' | 'system' | undefined =
    rawSenderType === 'user' || rawSenderType === 'contact' || rawSenderType === 'system'
      ? rawSenderType : undefined;

  if (!derivedSenderType) {
    if (messageType === 'system' || rawSenderId === 'system') {
      derivedSenderType = 'system';
    } else if (typeof rawSenderId === 'string' && (/^\+?\d{10,15}$/.test(rawSenderId) || rawSenderId.startsWith('contact_'))) {
      derivedSenderType = 'contact';
    } else if (deliveryChannel === 'sms' || deliveryChannel === 'mms') {
      if (!rawSenderId || !(typeof rawSenderId === 'string' && (rawSenderId.startsWith('team_') || rawSenderId.startsWith('user_')))) {
        derivedSenderType = 'contact';
      } else {
        derivedSenderType = 'user';
      }
    } else {
      derivedSenderType = 'user';
    }
  }

  return { rawSenderId, derivedSenderType };
};

/**
 * Primary normalizer used by both HTTP and WebSocket flows.
 *
 * Input sources:
 *   - REST responses: Schema<'MessageResponse'> (camelCase)
 *   - WebSocket payloads: MessageCreatedPayload (nested under `message`)
 *   - Legacy/team-chat data: may carry snake_case fields
 */
export const normalizeMessage = (input: RawMessageInput): Message => {
  // Prefer nested message field when present in socket payload
  const src: RawMessageInput = (input.message && typeof input.message === 'object')
    ? input.message
    : input;

  const attachments = normalizeAttachments(src);
  const { rawSenderId, derivedSenderType } = deriveSender(src);
  // Ensure content is always a string — backend may send objects like { text: "..." }
  const rawContent = src.content ?? (typeof src.message === 'string' ? src.message : null) ?? '';
  const content: string = typeof rawContent === 'string'
    ? rawContent
    : (typeof rawContent === 'object' && rawContent !== null
        ? (rawContent.text || rawContent.message || '').toString()
        : String(rawContent));

  // Build a consistent sender object regardless of backend shape
  let normalizedSender: Message['sender'] | undefined = undefined;
  const rawSender = src.sender || src.senderSnapshot;
  if (rawSender) {
    const type = rawSender.type as 'user' | 'contact' | 'system' | undefined;
    const id = rawSender.id || rawSender.senderId;

    if (type === 'user') {
      normalizedSender = {
        id: id || '',
        type: 'user',
        firstName: rawSender.firstName,
        lastName: rawSender.lastName,
        fullName: rawSender.fullName,
        email: rawSender.email,
        avatarColor: rawSender.avatarColor,
        profileImageUrl: rawSender.profileImageUrl,
      };
    } else if (type === 'contact') {
      normalizedSender = {
        id: id || '',
        type: 'contact',
        firstName: rawSender.firstName,
        lastName: rawSender.lastName,
        fullName: rawSender.fullName,
        phoneNumber: rawSender.phoneNumber,
        profileImageUrl: rawSender.profileImageUrl,
      };
    } else if (type === 'system') {
      normalizedSender = { id: '', type: 'system', fullName: rawSender.fullName || 'Auto-Reply' };
    }
  }

  const normalized: Message = {
    id: src.id || '',
    conversationId: src.conversationId || input.conversationId || '',
    senderId: rawSenderId || '',
    senderType: derivedSenderType || 'user',
    content,
    messageType: src.messageType || 'text',
    deliveryChannel: src.deliveryChannel || 'internal',
    timestamp: getMessageTimestamp(src),
    sentAt: src.sentAt instanceof Date ? src.sentAt.toISOString() : (src.sentAt ?? undefined),
    status: src.status || 'pending',
    isLogged: src.isLogged || false,
    attachments,
    metadata: src.metadata || {},
    sender: normalizedSender ?? src.sender as Message['sender']
  } as Message;

  // Pass through subtype/data for system (activity) messages
  if (isSystemMessage(normalized)) {
    if (src.subtype) normalized.subtype = src.subtype;
    if (src.data) normalized.data = src.data;
    // Also check metadata for API-loaded messages that store activity info
    const meta = src.metadata as { activitySubtype?: string; activityData?: Record<string, unknown> } | undefined;
    if (!src.subtype && meta?.activitySubtype) {
      normalized.subtype = meta.activitySubtype;
      normalized.data = meta.activityData;
    }
  }

  return normalized;
};

export const hasRenderableContent = (msg: Message): boolean => {
  const hasText = typeof msg.content === 'string' && msg.content.trim().length > 0;
  const hasFiles = Array.isArray(msg.attachments) && msg.attachments.length > 0;
  return hasText || hasFiles;
};
